<script setup>
import { computed } from 'vue';
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Tooltip,
  LinearScale,
  CategoryScale,
  BarElement,
  BarController,
} from 'chart.js';
import {
  useThemeTick,
  color,
} from 'dashboard/components-next/analytics/composables/useThemeColors';

// Uma série só, uma cor só: o gráfico responde "quanto por dia", e o título já
// diz de qual produto. Sem segundo eixo e sem legenda — os dois só fariam
// sentido se houvesse mais de uma medida aqui dentro.
const props = defineProps({
  timeline: { type: Array, default: () => [] }, // [{ date, total }]
  colorVar: { type: String, default: '--pz-chart-brand' },
  emptyLabel: { type: String, default: '' },
});

// Canvas não lê CSS var: os tokens são lidos já resolvidos, e `tick` entra no
// objeto para que uma virada de tema invalide o computed e redesenhe.
const tick = useThemeTick();

const tokens = computed(() => ({
  tick: tick.value,
  accent: color(props.colorVar, '#a21caf'),
  axis: color('--pz-chart-muted', '#8b8d98'),
  border: color('--pz-chart-border', '#d9d9e0'),
  grid: color('--pz-chart-border-soft', '#ececef'),
  card: color('--pz-chart-card', '#fff'),
  text: color('--pz-chart-text', '#1c2024'),
  textSoft: color('--pz-chart-text-2', '#60646c'),
}));

const nf = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

const hasData = computed(() => props.timeline.some(row => row.total > 0));

const data = computed(() => {
  const { accent } = tokens.value;
  return {
    labels: props.timeline.map(row => row.date),
    datasets: [
      {
        data: props.timeline.map(row => row.total),
        backgroundColor: accent,
        hoverBackgroundColor: accent,
        // Topo arredondado ancorado na base, barra fina com respiro entre elas.
        borderRadius: 4,
        borderSkipped: 'bottom',
        categoryPercentage: 0.86,
        barPercentage: 0.86,
        maxBarThickness: 28,
      },
    ],
  };
});

const options = computed(() => {
  const { axis, border, grid, card, text, textSoft } = tokens.value;
  return {
    responsive: true,
    maintainAspectRatio: false,
    // 'index' com intersect false: o alvo do hover é a coluna inteira, não os
    // poucos pixels da barra.
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: {
        grid: { display: false },
        border: { color: border },
        ticks: {
          color: axis,
          font: { size: 11 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 10,
        },
      },
      y: {
        beginAtZero: true,
        border: { display: false },
        grid: { color: grid, drawTicks: false },
        ticks: {
          color: axis,
          font: { size: 11 },
          maxTicksLimit: 4,
          padding: 8,
          callback: v => nf.format(v),
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: card,
        titleColor: text,
        bodyColor: textSoft,
        borderColor: border,
        borderWidth: 1,
        padding: 10,
        cornerRadius: 10,
        displayColors: false,
        callbacks: { label: c => nf.format(c.parsed.y) },
      },
    },
  };
});

ChartJS.register(
  Tooltip,
  LinearScale,
  CategoryScale,
  BarElement,
  BarController
);
</script>

<template>
  <div class="relative w-full h-[180px]">
    <Bar v-if="hasData" :data="data" :options="options" />
    <div
      v-else
      class="flex justify-center items-center h-full text-sm text-n-slate-10"
    >
      {{ emptyLabel }}
    </div>
  </div>
</template>
